import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

/*
 * FacilityGallery
 * ---------------
 * Renders a responsive gallery of facility photos fetched from the backend
 * endpoint '/facility-photos/'. Each image is wrapped with LightGallery to
 * provide a lightbox view with thumbnail and zoom plugins.
 *
 * Behavior / notes:
 * - Fetches data on mount and treats any network or parse errors as an
 *   empty array.
 * - The API is expected to return an array of objects with a 'photo' field
 *   containing a server-relative path such as '/media/fac/1.jpg'. We compose
 *   the full URL using the 'url' base constant below.
 */

import config from '../config';
import api from '../api';
const url = config.API_URL;

function FacilityGallery() {
  const [facilityPhotos, setFacilityPhotos] = useState([]);

  useEffect(() => {
    api.get('/facility-photos/')
      .then((res) => {
        const data = res && res.data ? res.data : [];
        if (Array.isArray(data)) {
          setFacilityPhotos(data);
        } else {
          setFacilityPhotos([]);
        }
      })
      .catch(() => {
        setFacilityPhotos([]);
      });
  }, []);

  const [selectedIdx, setSelectedIdx] = useState(null);

  const openAt = useCallback((i) => setSelectedIdx(i), []);
  const close = useCallback(() => setSelectedIdx(null), []);

  const goto = useCallback((delta) => {
    setSelectedIdx((cur) => {
      if (cur === null) return null;
      const next = (cur + delta + facilityPhotos.length) % facilityPhotos.length;
      return next;
    });
  }, [facilityPhotos.length]);

  useEffect(() => {
    if (selectedIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') goto(-1);
      if (e.key === 'ArrowRight') goto(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedIdx, close, goto]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginTop: '32px'
      }}
    >
      {facilityPhotos.map((photoObj, idx) => {
        const raw = photoObj && photoObj.photo ? String(photoObj.photo).trim() : '';
        let photoUrl = raw
          ? (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('//')
              ? raw
              : url + (raw.startsWith('/') ? raw : '/' + raw))
          : '';

        // Optimize images if they're from Cloudinary
        if (photoUrl.includes('cloudinary.com')) {
          // Add Cloudinary transformations: auto format, quality, and responsive sizing
          photoUrl = photoUrl.replace('/upload/', '/upload/f_auto,q_auto,w_600,c_limit/');
        }

        const altText = photoObj.alt_text || photoObj.caption || `Facility View ${idx + 1}`;
        // First image should load eagerly with high priority (LCP candidate)
        const isFirstImage = idx === 0;
        
        return (
          <div
            key={idx}
            style={{
              display: 'block',
              width: '100%',
              borderRadius: '8px',
              overflow: 'hidden',
              cursor: 'pointer',
              aspectRatio: '4/3' // Reserve space to prevent layout shift
            }}
            onClick={() => openAt(idx)}
            data-testid={`facility-thumb-${idx}`}
          >
            <img
              src={photoUrl}
              alt={altText}
              loading={isFirstImage ? 'eager' : 'lazy'}
              fetchPriority={isFirstImage ? 'high' : undefined}
              width="600"
              height="450"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </div>
        );
      })}

      <Dialog
        open={selectedIdx !== null}
        onClose={close}
        maxWidth="lg"
        fullWidth
        data-testid="facility-dialog"
      >
        <IconButton
          aria-label="close"
          onClick={close}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1200 }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ position: 'relative', p: 0, background: '#000' }}>
          {selectedIdx !== null && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <IconButton
                aria-label="previous"
                onClick={() => goto(-1)}
                sx={{ position: 'absolute', left: 8, color: '#fff', zIndex: 1 }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>

              {(() => {
                let fullPhotoUrl = (facilityPhotos[selectedIdx] && facilityPhotos[selectedIdx].photo) ?
                  ((String(facilityPhotos[selectedIdx].photo).startsWith('http') || String(facilityPhotos[selectedIdx].photo).startsWith('//'))
                    ? facilityPhotos[selectedIdx].photo
                    : url + (String(facilityPhotos[selectedIdx].photo).startsWith('/') ? String(facilityPhotos[selectedIdx].photo) : '/' + String(facilityPhotos[selectedIdx].photo)))
                  : '';
                
                // Optimize lightbox images from Cloudinary
                if (fullPhotoUrl.includes('cloudinary.com')) {
                  fullPhotoUrl = fullPhotoUrl.replace('/upload/', '/upload/f_auto,q_auto,w_1920,c_limit/');
                }
                
                return (
                  <img
                    src={fullPhotoUrl}
                    alt={facilityPhotos[selectedIdx] && (facilityPhotos[selectedIdx].alt_text || facilityPhotos[selectedIdx].caption) ?
                      (facilityPhotos[selectedIdx].alt_text || facilityPhotos[selectedIdx].caption) : `Facility View ${selectedIdx + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '80vh', display: 'block', margin: '0 auto' }}
                  />
                );
              })()}

              <IconButton
                aria-label="next"
                onClick={() => goto(1)}
                sx={{ position: 'absolute', right: 8, color: '#fff', zIndex: 1 }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FacilityGallery;
